// Scales
import { equityIndicatorPctBlackThresholdScale, equityIndicatorPopDensityThresholdScale, 
         equityIndicatorPctHispanicThresholdScale, theme } from "./Theme";

// Types
import { State, County, Tract, Bounds, VotingDistrict, PollingLoc } from "./Types";
import { LatLng } from "leaflet";
import { Feature } from "geojson";
import { currentStates } from "./Global";

function findPollingLocsByChangeYear(ei: any) {

    return {
        missingDataFlag: ei.missingDataFlag === undefined? false: ei.missingDataFlag,
        status: {
            pollsRemoved: ei.pollsRemoved,
            pollsAdded: ei.pollsAdded,
            pollsNoChange: ei.pollsNoChange,
            baseYearPollingLocationsTotal: ei.baseYearPollingLocationsTotal,
            changeNoPolls: ei.changeNoPolls,
            overallChange: ei.overallChange,
            baseYearNoPollsMissingLatLng: ei.baseyearnopollsmissinglatlng,
            lastGEYearNoPollsMissingLatLng: ei.lastGEYearNoPollsMissingLatLng,
            baseYearCVAPPerPoll: ei.baseYearCVAPPerPoll,
            changeYearCVAPPerPoll: ei.changeYearCVAPPerPoll,
        }
    }
}

// Returns the equity measure for the selected equity indicator
function getChangeYearData(ei: any, missingDataFlagALAND?: boolean) {

    if (ei !== undefined) {
        let baseYearPctCVAPBlack = {equityMeasure: missingDataFlagALAND? -1: ei!.baseYearPctCVAPBlack, 
                                    fillColor: missingDataFlagALAND? theme.missingDataFill: equityIndicatorPctBlackThresholdScale(ei.baseYearPctCVAPBlack) as string,
                                    missingDataFlag: missingDataFlagALAND}

        let baseYearPctCVAPHispanic = {equityMeasure: missingDataFlagALAND? -1: ei!.baseYearPctCVAPHispanic, 
                                       fillColor: missingDataFlagALAND? theme.missingDataFill: equityIndicatorPctHispanicThresholdScale(ei.baseYearPctCVAPHispanic) as string,
                                       missingDataFlag: missingDataFlagALAND}

        let baseYearPopDensity = {equityMeasure: missingDataFlagALAND? -1: ei!.baseYearPopDensity, 
                                  fillColor: missingDataFlagALAND? theme.missingDataFill: equityIndicatorPopDensityThresholdScale(ei.baseYearPopDensity) as string,
                                  missingDataFlag: missingDataFlagALAND}
        let pollSummary = findPollingLocsByChangeYear(ei);

        return { baseYearPctCVAPBlack: baseYearPctCVAPBlack,
                 baseYearPctCVAPHispanic: baseYearPctCVAPHispanic,
                 baseYearPopDensity: baseYearPopDensity,
                 pollSummary: pollSummary
        };

    } else {
        return undefined;
    }
}

// Structures the bounds for each geometry
function getBounds(d: any) {
    return {northEast: {lat: d.ymax, lng: d.xmin} as LatLng,
            southWest: {lat: d.ymin, lng: d.xmax} as LatLng } as Bounds;
}

// Structures lat long object
function getLatLng(d: any) {
    return {lat: d.Y, lng: d.X} as LatLng;
}

// Refturns a feature collection 
function returnFeatureCollection(features: Feature[]) {
    return {type: 'FeatureCollection', features: features as GeoJSON.Feature[]} as GeoJSON.FeatureCollection;
}

export function getStates(data: any) {

    const stateFeatures = [] as GeoJSON.Feature[];

    (data as any[]).forEach((d: any) => {

        stateFeatures.push({type: 'Feature', 
            properties: {type: 'State',
                         descr: '',
                         name: d.name,
                         stfp: d.stfp,
                         latlng: getLatLng(d),
                         zoom: d.stfp === "45"? 8: 7,
                         stabbr: d.stabbr,
                         bounds: getBounds(d),
                         selected: true} as State, 
            geometry: d.geometry as GeoJSON.Geometry})
    });

    return returnFeatureCollection(stateFeatures);
}

// Returns a feature collection of all the counties for the selected project states
export function getCounties(countiesGeo: any[], countiesLong: any[]) {

    const features: Feature[] = [];

    (countiesGeo as any[]).forEach((d: any) => {

        const ei = countiesLong.find((p: any) => p.cntyfp === d.cntyfp);

        features.push({type: 'Feature',
            properties: {type: 'County',
                         descr: 'County',
                         name: d.name,
                         cntyfp: d.cntyfp,
                         stfp: d.stfp,
                         latlng: getLatLng(d),
                         zoom: 10,
                         selected: true,
                         changeYearData: getChangeYearData(ei),
                         bounds: getBounds(d),
                         stabbr: d.stabbr
                        } as County, 
            geometry: d.geometry as GeoJSON.Geometry})
    });

    return returnFeatureCollection(features);
}

// Returns a feature collection of all the tracts for the selected project states
export function getTracts(data: any[], tractsLong: any[]) {

    const features: Feature[] = [];

    (data as any[])
        .forEach((d: any) => {

            const ei = tractsLong.find((p: any) => p.tractfp === d.tractfp);

            features.push({type: 'Feature', 
                properties: {
                    type: 'Tract',
                    descr: 'Census tract',
                    name: d.name,
                    stfp: d.stfp,
                    cntyfp: d.cntyfp,
                    tractfp: d.tractfp,
                    latlng: getLatLng(d),
                    zoom: 12,
                    selected: true,
                    changeYearData: getChangeYearData(ei, d.missingDataFlagALAND),
                    bounds: getBounds(d),
                } as Tract,
                geometry: d.geometry as GeoJSON.Geometry})
        });

    return {type: 'FeatureCollection', features: features as GeoJSON.Feature[]} as GeoJSON.FeatureCollection;
}

export function getVd(data: any[]) {

    const features: Feature[] = [];

    (data as any[]).forEach((d: any) => {

        features.push({type: 'Feature', 
            properties: {type: 'Voting district',
                         descr: 'Voting district',
                         name: d.name,
                         stfp: d.stfp, 
                         cntyfp: d.cntyfp,
                         vtdst: d.vtdst,
                         selected: true,
                         latlng: getLatLng(d),
                         bounds: getBounds(d)
                        } as VotingDistrict, 
            geometry: d.geometry as GeoJSON.Geometry})
    });

    return returnFeatureCollection(features);
}

export function dmCountyLongData(data: any[]) {

    const counties: any[] = [];

    (data as any[])
        .filter((d: any) => currentStates.includes(d.stfp))
        .forEach((d: any) => {
            counties.push({
                cntyfp: d.cntyfp.toString(), 
                stfp: d.stfp.toString(),
                changeYear: d.changeyear,
                baseYearPctCVAPBlack: Math.round(+d.baseyearpctcvapblack*100),
                baseYearPctCVAPWhite: Math.round(+d.baseyearpctcvapwhite*100),
                baseYearPctCVAPHispanic: Math.round(+d.baseyearpctcvaphispanic*100),
                baseYearPopDensity: Math.round(+d.baseyearpopdensity),
                baseYearCVAPPerPoll: d.baseyearcvapperpoll === null ? undefined : +d.baseyearcvapperpoll,
                changeYearCVAPPerPoll: d.changeyearcvapperpoll === null ? undefined : +d.changeyearcvapperpoll,
                baseYearPollingLocationsTotal: +d.baseyearpollinglocstotal === undefined? 0: +d.baseyearpollinglocstotal,
                baseYearNoPollsMissingLatLng: +d.baseyearnopollsmissinglatlng === undefined? 0: +d.baseyearnopollsmissinglatlng,
                lastGEYearNoPollsMissingLatLng: +d.lastgeyearnopollsmissinglatlng === undefined? 0: +d.lastgeyearnopollsmissinglatlng,
                changeNoPolls: +d.changenopolls,
                pollsRemoved: +d.nopollsremoved,
                pollsAdded: +d.nopollsadded,
                pollsNoChange: +d.pollsnochange,
                overallChange: +d.nopollsadded - +d.nopollsremoved,
                missingDataFlag: d.missingdataflag === "True"
                
            });
    });

    return counties;
}

export function dmTractLongData(data: any[]) {

    const tracts: any[] = [];

    (data as any[])
        .filter((d: any) => currentStates.includes(d.stfp))
        .forEach((d: any) => {
            tracts.push({
                cntyfp: d.cntyfp.toString(), 
                stfp: d.stfp.toString(),
                tractfp: d.tractfp.toString(),
                changeYear: d.changeyear,
                baseYearPctCVAPBlack: Math.round(+d.baseyearpctcvapblack*100),
                baseYearPctCVAPHispanic: Math.round(+d.baseyearpctcvaphispanic*100),
                baseYearPopDensity: Math.round(+d.baseyearpopulationdensity),
                baseYearPollingLocationsTotal: +d.baseyearpollinglocstotal,
                changeNoPolls: +d.changenopolls,
                pollsRemoved: +d.nopollsremoved,
                pollsAdded: +d.nopollsadded,
                pollsNoChange: +d.pollsnochange,
                overallChange: +d.nopollsadded - +d.nopollsremoved,
                missingDataFlag: d.missingdataflag === "True",
                baseYearNoPollsMissingLatLng: +d.baseyearnopollsmissinglatlng === undefined? 0: +d.baseyearnopollsmissinglatlng,
                lastGEYearNoPollsMissingLatLng: +d.lastgeyearnopollsmissinglatlng === undefined? 0: +d.lastgeyearnopollsmissinglatlng,
            });
    });

    return tracts;
}

// Get Polling Locations
export function dmPollingLocsLongData(data: any[]) {

    const pollingLoc: PollingLoc[] = [];

    (data as any[])
        .filter((d: any) => currentStates.includes(d.stfp))
        .forEach((d: any) => {

            pollingLoc.push({
                    type: 'Poll',
                    descr: 'Polling location',
                    stfp: d.stfp.toString(),
                    cntyfp: d.cntyfp.toString(),
                    tractfp: d.tractfp.toString(),
                    changeYear: d.changeyear,
                    name: d.name,
                    latlng: { lat: +d.y, lng: +d.x } as LatLng,
                    status: d.status === "no_change"? "No change": d.status === "added"? "Added": "Removed",
                    missingDataFlag: d.missingdataflag === "True"
                } as PollingLoc );
    });
    return pollingLoc;
}

// Stack Overflow Link: https://stackoverflow.com/questions/27979002/convert-csv-data-into-json-format-using-javascript
export function csvJSON(csv: any) {
    const lines = csv.split('\n')
    const result: any[] = []
    const headers = lines[0].split(',')
  
    for (let i = 1; i < lines.length; i++) {        
        if (!lines[i])
            continue
        const obj: { [key: string]: string } = {}
        const currentline = lines[i].split(',')
  
        for (let j = 0; j < headers.length; j++) {
            obj[headers[j]] = currentline[j]
        }
        result.push(obj)
    }
    return result
}
